import { useMemo, useState, useEffect, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import JsonLd from '@/components/feature/JsonLd';
import ArticleCard from '@/components/feature/ArticleCard';
import { useSeo } from '@/hooks/useSeo';
import { articles } from '@/mocks/articles';
import { categories, brand } from '@/mocks/site';

const iconMap: Record<string, string> = {
  ent: 'ri-movie-2-line',
  biz: 'ri-store-3-line',
  tech: 'ri-cpu-line',
  fin: 'ri-line-chart-line',
  soc: 'ri-community-line',
  opi: 'ri-chat-smile-2-line',
};

export default function Feed() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') || '';
  const initialCat = searchParams.get('cat') || 'all';

  const [keyword, setKeyword] = useState(initialQ);
  const [activeCat, setActiveCat] = useState(initialCat);
  const [sort, setSort] = useState<'latest' | 'hot'>('latest');

  useEffect(() => {
    setKeyword(searchParams.get('q') || '');
    setActiveCat(searchParams.get('cat') || 'all');
  }, [searchParams]);

  useSeo({
    title: '线报库 - 独家揭秘线报与图解内幕归档 | 揭秘局',
    description:
      '揭秘局线报库收录全部独家揭秘线报与深度内幕，支持按娱乐、商业、科技、财经、社会、文娱六个方向筛选，并可按关键词检索图解与后续反映。',
    keywords: '独家揭秘线报,独家揭秘深度内幕,线报库,图解,信息图,后续反映',
    path: '/feed',
  });

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const list = articles.filter((article) => {
      const matchCat = activeCat === 'all' || article.categoryId === activeCat;
      if (!matchCat) return false;
      if (!q) return true;
      const haystack = [
        article.title,
        article.excerpt,
        article.category,
        ...article.tags,
        ...article.keyPoints,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
    return list.sort((a, b) => (sort === 'hot' ? b.views - a.views : a.date < b.date ? 1 : -1));
  }, [keyword, activeCat, sort]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (keyword.trim()) next.set('q', keyword.trim());
    if (activeCat !== 'all') next.set('cat', activeCat);
    setSearchParams(next);
  };

  const handleCat = (catId: string) => {
    setActiveCat(catId);
    const next = new URLSearchParams();
    if (keyword.trim()) next.set('q', keyword.trim());
    if (catId !== 'all') next.set('cat', catId);
    setSearchParams(next);
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '线报库',
    description: '揭秘局全部独家揭秘线报与图解内幕归档。',
    inLanguage: 'zh-CN',
    isPartOf: { '@type': 'WebSite', name: brand.name },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: window.location.origin },
      { '@type': 'ListItem', position: 2, name: '线报库', item: `${window.location.origin}/feed` },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100 border-b border-background-200/70">
          <nav aria-label="面包屑" className="text-xs text-foreground-500">
            <ol className="flex items-center gap-2">
              <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line" /></li>
              <li className="text-foreground-800">线报库</li>
            </ol>
          </nav>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-black text-foreground-950">
            线报库 · 全部揭秘归档
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-foreground-600 leading-relaxed">
            这里收录揭秘局整理过的全部独家线报与深度内幕。可以按方向筛选，也可以直接搜索事件关键词，每条记录都保留后续反映与图解链接。
          </p>
        </section>

        <section className="px-4 md:px-6 py-8 md:py-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleCat('all')}
                className={`px-3.5 h-9 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  activeCat === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300'
                }`}
              >
                全部（{articles.length}）
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleCat(cat.id)}
                  className={`inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    activeCat === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300'
                  }`}
                >
                  <i className={`${iconMap[cat.id]} text-sm`} />
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <form onSubmit={handleSubmit} role="search" className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-3.5 h-10 flex-1 lg:w-64">
                <i className="ri-search-line text-sm text-foreground-500" />
                <input
                  type="search"
                  name="q"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索线报关键词"
                  aria-label="搜索线报关键词"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none"
                />
              </form>
              <div className="flex items-center rounded-full bg-background-100 border border-background-200 p-1">
                <button
                  type="button"
                  onClick={() => setSort('latest')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    sort === 'latest' ? 'bg-background-50 text-foreground-950' : 'text-foreground-600'
                  }`}
                >
                  最新
                </button>
                <button
                  type="button"
                  onClick={() => setSort('hot')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    sort === 'hot' ? 'bg-background-50 text-foreground-950' : 'text-foreground-600'
                  }`}
                >
                  最热
                </button>
              </div>
            </div>
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共找到 <strong className="text-primary-600">{filtered.length}</strong> 篇线报
            {activeCat !== 'all' && ` · ${categories.find((c) => c.id === activeCat)?.name}`}
            {keyword.trim() && ` · 关键词「${keyword.trim()}」`}
          </p>

          {filtered.length === 0 ? (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="inline-flex w-14 h-14 rounded-full bg-background-200 text-foreground-500 items-center justify-center">
                <i className="ri-file-search-line text-2xl" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">暂时没有匹配的线报</h3>
              <p className="mt-2 text-sm text-foreground-600">换个关键词试试，或者切换到"全部"看看完整归档。</p>
              <button
                type="button"
                onClick={() => { setKeyword(''); handleCat('all'); }}
                className="mt-5 inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                重置筛选
              </button>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
      <JsonLd id="ld-feed-collection" data={collectionSchema} />
      <JsonLd id="ld-feed-breadcrumb" data={breadcrumbSchema} />
    </div>
  );
}